import {
    InvitationMethodField,
    invitationMethodFor,
} from 'src/components/AccountAccess/InvitationMethodField';
import { useHistory } from 'react-router-dom';
import container from 'src/container';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import {
    UserRole,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { useCurrentConsumerContext } from '../Home/NewHome/reducer';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import Input, {
    Error as InputError,
} from 'src/components/FormComponents/Input';
import { PeopleSVG } from 'src/assets/svg';
import Form from 'src/components/FormComponents/Form';
import { EnvelopeIcon } from 'src/components/Icons/icons';
import PhoneInput from 'src/components/FormComponents/PhoneInput';
import CheckBox from 'src/components/FormComponents/CheckBox';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganizationId } from 'src/view/hooks';
import {
    NextOfKinFormSchema,
    NextOfKinFormValues,
} from '../NextOfKin/nextOfKin.schema';
import { NextOfKinRelationType } from 'src/domain/next-of-kin';

const {
    cradle: { munduClient },
} = container;

export const NewNextOfKin = () => {
    const history = useHistory();
    const {
        state: { currentConsumer },
    } = useCurrentConsumerContext();
    const notify = useNotification();
    const user = useSignedInUser();
    const organizationId = useCurrentOrganizationId();
    const role = getPrimaryRole(user);
    const isOrganization = isOrganizationAdministrator(user);
    const { t } = useTranslation();
    if (!user || !currentConsumer || !organizationId) return null;

    const handleSubmit = async ({
        firstName,
        lastName,
        phoneNumber,
        email,
        invitationMethod,
        relation,
        isMainContact,
        isGuardian,
        disableManagingRelatives,
    }: NextOfKinFormValues) => {
        try {
            if (!email?.trim() && !phoneNumber)
                throw new Error(t('accountAccess.invitation.contactRequired'));

            await munduClient.createNextofKin({
                organizationId,
                phoneNumber: phoneNumber ? phoneNumber : undefined,
                email: email?.trim() || undefined,
                invitationMethod: invitationMethodFor(
                    email?.trim(),
                    phoneNumber,
                    invitationMethod,
                ),
                profile: { firstName, lastName },
                followers: {
                    consumerIds: [currentConsumer.id],
                    creatorIds:
                        role === UserRole.creator && !isOrganization
                            ? [user.id]
                            : undefined,
                },
                relation,
                isMainContact,
                isGuardian,
                disableManagingRelatives: isMainContact
                    ? disableManagingRelatives ?? false
                    : false,
            });

            notify(t('nextOfKin.success'));
            history.goBack();
        } catch (e) {
            if (!(e instanceof Error)) {
                notify(t('errors.unknownLogin'), true);
                return;
            }

            if (
                e.message.includes(
                    'Pårørende er allerede knyttet til denne beboeren',
                )
            ) {
                notify(t('nextOfKin.notifications.alreadyLinked'), true);
                return;
            }

            if (
                e.message.includes(
                    'En bruker med denne eposten eksisterer allerede',
                )
            ) {
                notify(
                    t('nextOfKin.notifications.emailBelongsToOtherRole'),
                    true,
                );
                return;
            }

            notify(e.message, true);
        }
    };

    return (
        <div className="flex flex-col max-w-3xl mx-auto">
            <Section title={t('nextOfKin.title')}>
                <Form
                    className="bg-lightPeach text-woodyBrown"
                    schema={NextOfKinFormSchema}
                    onSubmit={handleSubmit}
                    mode="onChange"
                    shouldUnregister
                >
                    {({
                        register,
                        control,
                        watch,
                        formState: { errors, isSubmitting },
                    }) => (
                        <div className="grid grid-cols-2 gap-4 h-full">
                            <div className="col-span-full flex justify-center">
                                {/* TODO: Replace with Role icon when ready */}
                                <div className="rounded-full w-36 h-36 bg-starkWhite flex justify-center items-center mb-4">
                                    <PeopleSVG className="w-[18px] h-[18px]" />
                                </div>
                            </div>
                            <Input
                                className="text-input"
                                type="text"
                                label={t('nextOfKin.form.firstName')}
                                error={errors.firstName}
                                {...register('firstName')}
                            />
                            <Input
                                className="text-input"
                                type="text"
                                label={t('nextOfKin.form.lastName')}
                                error={errors.lastName}
                                {...register('lastName')}
                            />
                            <Input
                                className="text-input"
                                type="email"
                                label={t('nextOfKin.form.email')}
                                error={errors.email}
                                leadingIcon={EnvelopeIcon}
                                {...register('email')}
                            />
                            <PhoneInput
                                className="text-input"
                                type="tel"
                                error={errors.phoneNumber}
                                label={t('nextOfKin.form.phone')}
                                {...register('phoneNumber')}
                            />

                            <InvitationMethodField control={control} />

                            <div>
                                <label className="mb-1 block text-sm font-medium text-woodyBrown">
                                    {t('nextOfKin.form.relationLabel')}
                                </label>
                                <select
                                    className="text-input w-full"
                                    defaultValue=""
                                    aria-invalid={Boolean(errors.relation)}
                                    {...register('relation')}
                                >
                                    <option value="">
                                        {t('nextOfKin.form.selectRelation')}
                                    </option>
                                    <option
                                        value={NextOfKinRelationType.family}
                                    >
                                        {t('nextOfKin.form.relationFamily')}
                                    </option>
                                    <option
                                        value={NextOfKinRelationType.external}
                                    >
                                        {t('nextOfKin.form.relationGuardian')}
                                    </option>
                                </select>
                                <InputError error={errors.relation} />
                            </div>

                            <CheckBox
                                className="col-span-full"
                                label={t('nextOfKin.form.isMainContactLabel')}
                                {...register('isMainContact')}
                            />
                            <CheckBox
                                className="col-span-full"
                                label={t('nextOfKin.form.isGuardianLabel')}
                                {...register('isGuardian')}
                            />

                            {watch('isMainContact') && isOrganization && (
                                <CheckBox
                                    className="col-span-full"
                                    label={t(
                                        'nextOfKin.form.disableManagingRelativesLabel',
                                    )}
                                    {...register('disableManagingRelatives')}
                                />
                            )}

                            <div className="flex items-center gap-6">
                                <Input
                                    value={t('nextOfKin.form.submit')}
                                    className="primary-btn-small"
                                    disabled={isSubmitting}
                                    type="submit"
                                />
                            </div>
                        </div>
                    )}
                </Form>
            </Section>
        </div>
    );
};
